import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Layout from "../../Layout.tsx";
import Modal from "../../Modal.tsx";
import PlannerViewSwitch from "../../PlannerViewSwitch.tsx";
import { selectedFilteredVessel } from "../../../selectors/filterSelector.js";
import { getVessels } from "../../../data/vessels.js";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  CalendarViewMonthOutlined as CalendarViewMonthOutlinedIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Download as DownloadIcon,
  EditOutlined as EditOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  LocalGasStationOutlined as LocalGasStationOutlinedIcon,
  OpacityOutlined as OpacityOutlinedIcon,
} from "@mui/icons-material";

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import GasoilDialogForm from "./GasoilDialogForm";
import BunkeringDialogForm from "./BunkeringDialogForm";
import GasoilYearOverviewDialog from "./GasoilYearOverviewDialog";

type DashboardUser = {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  email?: string | null;
};

type DashboardFile = {
  "@id"?: string;
  id?: number;
  name?: string | null;
  originalName?: string | null;
  contentUrl?: string | null;
  path?: string | null;
};

type DashboardMeasurement = {
  "@id"?: string;
  id?: number;
  amount?: number | null;
  date?: string | null;
  updatedAt?: string | null;
  user?: DashboardUser | string | null;
};

type DashboardBunkering = {
  "@id"?: string;
  id?: number;
  amount?: number | null;
  date?: string | null;
  supplier?: string | null;
  file?: DashboardFile | string | null;
  user?: DashboardUser | string | null;
};

type DashboardChartRow = {
  key: string;
  label: string;
  consumed: number;
  bunkered: number;
};

type VesselGasoilDashboard = {
  vessel?: {
    id?: number | string;
    name?: string | null;
  } | null;
  currentOnboard?: DashboardMeasurement | null;
  lastBunkering?: DashboardBunkering | null;
  staleDays?: number | null;
  chart?: DashboardChartRow[];
  measurements?: DashboardMeasurement[];
  bunkerings?: DashboardBunkering[];
};

type TimelineItem = {
  key: string;
  type: "gasoil" | "bunkering";
  date?: string | null;
  amount?: number | null;
  title: string;
  subtitle: string;
  user?: DashboardUser | string | null;
  supplier?: string | null;
};

type DeleteTarget =
  | { type: "gasoil"; item: DashboardMeasurement }
  | { type: "bunkering"; item: DashboardBunkering }
  | null;

type PreviewFile = {
  url: string;
  name: string;
};

const API_URL = process.env.REACT_APP_API_URL;
const APP_URL = process.env.REACT_APP_URL || "";

const WRAPPER_BORDER_RADIUS = "12px";
const GASOIL_COLOR = "#0B2E78";
const BUNKERING_COLOR = "#16a34a";

const normalizeCollection = (value: any) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const getEntityId = (value?: any) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.split("/").filter(Boolean).pop() || value;
  }

  if (value["@id"]) {
    return String(value["@id"]).split("/").filter(Boolean).pop() || value["@id"];
  }

  if (value.id !== undefined && value.id !== null) {
    return String(value.id);
  }

  return "";
};

const getApiIri = (resource: string, id?: string | number | null) => {
  if (!id) return "";

  const stringId = String(id);

  if (stringId.startsWith("/api/")) {
    return stringId;
  }

  if (stringId.startsWith("http://") || stringId.startsWith("https://")) {
    return stringId;
  }

  return `/api/${resource}/${stringId}`;
};

const getVesselId = (vessel?: any) => {
  return getEntityId(vessel);
};

const getVesselName = (vessel?: any) => {
  return vessel?.name || vessel?.abbreviation || "";
};

const numberFormat = new Intl.NumberFormat("nl-BE", {
  maximumFractionDigits: 2,
});

const formatNumber = (value?: number | null) => {
  const parsed = Number(value ?? 0);

  return numberFormat.format(Number.isFinite(parsed) ? parsed : 0);
};

const getNumberValue = (value?: number | null) => {
  const parsed = Number(value ?? 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateOnly = (value?: string | null) => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "—";
};

const formatTimelineTime = (value?: string | null) => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("HH:mm") : "—";
};

const formatTimelineDate = (value?: string | null) => {
  if (!value) return "—";

  const date = dayjs(value);

  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
};

const getWeekKey = (value?: string | null) => {
  const date = dayjs(value);

  if (!date.isValid()) {
    return "";
  }

  return date.startOf("week").format("YYYY-MM-DD");
};

const buildWeeklyChartRows = ({
  measurements,
  bunkerings,
  weeks = 8,
}: {
  measurements: DashboardMeasurement[];
  bunkerings: DashboardBunkering[];
  weeks?: number;
}): DashboardChartRow[] => {
  const rows = new Map<string, DashboardChartRow>();

  for (let index = weeks - 1; index >= 0; index -= 1) {
    const weekStart = dayjs().startOf("week").subtract(index, "week");
    const key = weekStart.format("YYYY-MM-DD");

    rows.set(key, {
      key,
      label: weekStart.format("DD/MM"),
      consumed: 0,
      bunkered: 0,
    });
  }

  const sortedMeasurements = [...measurements]
    .filter((item) => item.date && dayjs(item.date).isValid())
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

  const sortedBunkerings = [...bunkerings].filter(
    (item) => item.date && dayjs(item.date).isValid()
  );

  sortedBunkerings.forEach((bunkering) => {
    const key = getWeekKey(bunkering.date);

    if (!rows.has(key)) {
      return;
    }

    const row = rows.get(key);

    if (!row) {
      return;
    }

    row.bunkered += getNumberValue(bunkering.amount);
  });

  for (let index = 1; index < sortedMeasurements.length; index += 1) {
    const previousMeasurement = sortedMeasurements[index - 1];
    const currentMeasurement = sortedMeasurements[index];

    const previousAmount = getNumberValue(previousMeasurement.amount);
    const currentAmount = getNumberValue(currentMeasurement.amount);

    const previousDate = dayjs(previousMeasurement.date);
    const currentDate = dayjs(currentMeasurement.date);

    const bunkeredBetweenMeasurements = sortedBunkerings
      .filter((bunkering) => {
        const bunkeringDate = dayjs(bunkering.date);

        return (
          bunkeringDate.isValid() &&
          bunkeringDate.isAfter(previousDate) &&
          (bunkeringDate.isBefore(currentDate) || bunkeringDate.isSame(currentDate))
        );
      })
      .reduce((total, bunkering) => total + getNumberValue(bunkering.amount), 0);

    const consumed = Math.max(
      previousAmount + bunkeredBetweenMeasurements - currentAmount,
      0
    );

    const key = getWeekKey(currentMeasurement.date);

    if (!rows.has(key)) {
      continue;
    }

    const row = rows.get(key);

    if (!row) {
      continue;
    }

    row.consumed += consumed;
  }

  return Array.from(rows.values()).map((row) => ({
    ...row,
    consumed: Math.round(row.consumed * 100) / 100,
    bunkered: Math.round(row.bunkered * 100) / 100,
  }));
};

const getUserLabel = (user?: DashboardUser | string | null) => {
  if (!user || typeof user === "string") return "—";

  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .join(" ");

  return fullName || user.username || user.email || "—";
};

const getFileUrl = (file?: DashboardFile | string | null) => {
  if (!file || typeof file === "string") return "";

  const contentUrl = file?.contentUrl?.trim();

  if (contentUrl) {
    if (
      contentUrl.startsWith("http://") ||
      contentUrl.startsWith("https://") ||
      contentUrl.startsWith("/") ||
      contentUrl.startsWith("blob:")
    ) {
      return contentUrl;
    }

    return `/files/${contentUrl.replace(/^\/+/, "")}`;
  }

  const path = file?.path?.trim();

  if (!path) return "";

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("/") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  return `/files/${path}`;
};

const getFileName = (
  file?: DashboardFile | string | null,
  fallback = "Document"
) => {
  if (!file || typeof file === "string") return fallback;

  return file.originalName || file.name || fallback;
};

const isPdfUrl = (url?: string | null) => {
  if (!url) return false;

  return /\.pdf($|\?)/i.test(url);
};

const getDeleteUrl = (target: DeleteTarget) => {
  if (!target) return "";

  const iri = target.item["@id"];

  if (iri) {
    return `${APP_URL}${iri}`;
  }

  const id = getEntityId(target.item);

  if (!id) return "";

  if (target.type === "gasoil") {
    return `${API_URL}/vessel_gasoils/${id}`;
  }

  return `${API_URL}/vessel_bunkerings/${id}`;
};

const getApiErrorMessage = (error: any, fallback: string) => {
  return (
    error?.response?.data?.["hydra:description"] ||
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const buildDashboard = ({
  vesselId,
  vesselName,
  measurements,
  bunkerings,
}: {
  vesselId?: string | number | null;
  vesselName?: string | null;
  measurements: DashboardMeasurement[];
  bunkerings: DashboardBunkering[];
}): VesselGasoilDashboard => {
  const sortedMeasurements = [...measurements].sort((a, b) => {
    return dayjs(b.date || 0).valueOf() - dayjs(a.date || 0).valueOf();
  });

  const sortedBunkerings = [...bunkerings].sort((a, b) => {
    return dayjs(b.date || 0).valueOf() - dayjs(a.date || 0).valueOf();
  });

  const currentOnboard = sortedMeasurements[0] || null;
  const lastBunkering = sortedBunkerings[0] || null;

  const staleDays =
    currentOnboard?.date && dayjs(currentOnboard.date).isValid()
      ? Math.max(dayjs().diff(dayjs(currentOnboard.date), "day"), 0)
      : null;

  return {
    vessel: {
      id: vesselId || undefined,
      name: vesselName || null,
    },
    currentOnboard,
    lastBunkering,
    staleDays,
    measurements: sortedMeasurements,
    bunkerings: sortedBunkerings,
  };
};

const buildTimelineItems = ({
  measurements,
  bunkerings,
  t,
}: {
  measurements: DashboardMeasurement[];
  bunkerings: DashboardBunkering[];
  t: any;
}): TimelineItem[] => {
  const measurementItems: TimelineItem[] = measurements.map((item) => ({
    key: `gasoil-${item["@id"] || item.id || item.date}`,
    type: "gasoil",
    date: item.date,
    amount: item.amount,
    title: t("measurement", "Measurement"),
    subtitle: getUserLabel(item.user),
    user: item.user,
  }));

  const bunkeringItems: TimelineItem[] = bunkerings.map((item) => ({
    key: `bunkering-${item["@id"] || item.id || item.date}`,
    type: "bunkering",
    date: item.date,
    amount: item.amount,
    title: t("bunkering", "Bunkering"),
    subtitle: item.supplier || "—",
    supplier: item.supplier,
    user: item.user,
  }));

  return [...measurementItems, ...bunkeringItems].sort((a, b) => {
    return dayjs(b.date || 0).valueOf() - dayjs(a.date || 0).valueOf();
  });
};

const groupTimelineItemsByDate = (items: TimelineItem[]) => {
  const map = new Map<string, TimelineItem[]>();

  items.forEach((item) => {
    const dateKey = formatTimelineDate(item.date);

    if (!map.has(dateKey)) {
      map.set(dateKey, []);
    }

    map.get(dateKey)?.push(item);
  });

  return Array.from(map.entries()).map(([date, entries]) => ({
    date,
    entries,
  }));
};

const StatCard: FC<{
  title: string;
  icon: React.ReactNode;
  value: string;
  subtitle?: string;
  secondaryLine?: string;
  accentColor: string;
}> = ({ title, icon, value, subtitle, secondaryLine, accentColor }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        p: 2.25,
        height: "100%",
        minHeight: 160,
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#1e293b" }}>
          {title}
        </Typography>

        <Avatar
          sx={{
            width: 44,
            height: 44,
            backgroundColor: `${accentColor}18`,
            color: accentColor,
          }}
        >
          {icon}
        </Avatar>
      </Stack>

      <Typography
        sx={{
          mt: 3.5,
          fontSize: 24,
          fontWeight: 950,
          color: accentColor,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>

      {subtitle ? (
        <Typography
          sx={{
            mt: 1.2,
            color: "text.secondary",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}

      {secondaryLine ? (
        <Typography
          sx={{
            mt: 1.2,
            fontSize: 14,
            fontWeight: 850,
            color: "#1e293b",
          }}
        >
          {secondaryLine}
        </Typography>
      ) : null}
    </Paper>
  );
};

const GasoilTimeline: FC<{
  items: TimelineItem[];
  loading: boolean;
  t: any;
}> = ({ items, loading, t }) => {
  const groupedItems = useMemo(() => groupTimelineItemsByDate(items), [items]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: WRAPPER_BORDER_RADIUS,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          p: 1.25,
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          backgroundColor: "#ffffff",
          flexShrink: 0,
        }}
      >
        <PlannerViewSwitch activeView="gasoil" disabled={loading} t={t} />
      </Box>

      {loading ? (
        <LinearProgress
          sx={{
            height: 3,
            flexShrink: 0,
          }}
        />
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 1,
        }}
      >
        {groupedItems.length ? (
          groupedItems.map((group) => (
            <Box key={group.date} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  px: 0.25,
                  mb: 1,
                  fontSize: "0.8rem",
                  fontWeight: 950,
                  color: "text.primary",
                }}
              >
                {group.date}
              </Typography>

              <Stack spacing={0.85}>
                {group.entries.map((item) => {
                  const isBunkering = item.type === "bunkering";
                  const color = isBunkering ? BUNKERING_COLOR : GASOIL_COLOR;
                  const backgroundColor = isBunkering
                    ? "rgba(22, 163, 74, 0.08)"
                    : "rgba(11, 46, 120, 0.08)";
                  const borderColor = isBunkering
                    ? "rgba(22, 163, 74, 0.18)"
                    : "rgba(11, 46, 120, 0.18)";

                  return (
                    <Paper
                      key={item.key}
                      elevation={0}
                      sx={{
                        minHeight: 78,
                        p: 0.95,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.9,
                        borderRadius: WRAPPER_BORDER_RADIUS,
                        backgroundColor,
                        border: `1px solid ${borderColor}`,
                        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.07)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          border: `2px solid ${color}`,
                          backgroundColor: "#ffffff",
                          color,
                        }}
                      >
                        {isBunkering ? (
                          <LocalGasStationOutlinedIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <OpacityOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.86rem",
                            fontWeight: 950,
                            color: "text.primary",
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1.15,
                          }}
                        >
                          {!isBunkering ? formatTimelineTime(item.date) : " "}
                        </Typography>

                        <Typography
                          noWrap
                          sx={{
                            mt: 0.35,
                            fontSize: "0.78rem",
                            fontWeight: 900,
                            color,
                          }}
                        >
                          {isBunkering ? "+" : ""}
                          {formatNumber(item.amount ?? 0)} L
                        </Typography>

                        <Typography
                          noWrap
                          sx={{
                            mt: 0.2,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            color: "text.secondary",
                          }}
                        >
                          {item.title}
                          {item.subtitle && item.subtitle !== "—"
                            ? ` • ${item.subtitle}`
                            : ""}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          ))
        ) : (
          <Alert severity="info">
            {t("noGasoilTimelineItems", "No gasoil timeline items found.")}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

const GasoilChartCard: FC<{
  rows: DashboardChartRow[];
}> = ({ rows }) => {
  const { t } = useTranslation();

  const hasData = rows.some(
    (row) => getNumberValue(row.consumed) > 0 || getNumberValue(row.bunkered) > 0
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        p: 2.25,
        height: "100%",
        minHeight: 300,
        backgroundColor: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: 16, fontWeight: 950, color: "#1e293b" }}>
            {t("weeklyConsumption", "Weekly consumption")}
          </Typography>

          <Tooltip
            title={t(
              "weeklyConsumptionTooltip",
              "Consumption is calculated from onboard measurements and bunkerings between measurements."
            )}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Tooltip>
        </Stack>

        <Chip
          label={t("lastEightWeeks", "Last 8 weeks")}
          variant="outlined"
          sx={{
            fontWeight: 850,
            borderRadius: "999px",
          }}
        />
      </Stack>

      <Box sx={{ width: "100%", height: 260 }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={rows}
              margin={{
                top: 12,
                right: 14,
                left: 4,
                bottom: 4,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{
                  fontSize: 12,
                  fontWeight: 700,
                }}
              />
              <YAxis
                tick={{
                  fontSize: 12,
                  fontWeight: 700,
                }}
                tickFormatter={(value) => `${formatNumber(Number(value))}`}
              />
              <RechartsTooltip
                formatter={(value: any, name: any) => [
                  `${formatNumber(Number(value))} L`,
                  name === "consumed"
                    ? t("consumed", "Consumed")
                    : t("bunkered", "Bunkered"),
                ]}
                labelFormatter={(label) => `${t("week", "Week")}: ${label}`}
              />
              <Legend
                formatter={(value) =>
                  value === "consumed"
                    ? t("consumed", "Consumed")
                    : t("bunkered", "Bunkered")
                }
              />
              <Bar
                dataKey="consumed"
                name="consumed"
                fill={GASOIL_COLOR}
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="bunkered"
                name="bunkered"
                fill={BUNKERING_COLOR}
                radius={[6, 6, 0, 0]}
              />
            </RechartsBarChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert severity="info">
              {t("noChartDataAvailable", "Not enough data to build the chart yet.")}
            </Alert>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const SectionCard: FC<{
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, children, footer }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 450,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.07)",
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 950, color: "#1e293b" }}>
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {children}
      </Box>

      {footer ? (
        <Box
          sx={{
            px: 2.5,
            pb: 2.5,
            pt: 1.5,
            mt: "auto",
            backgroundColor: "#ffffff",
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Paper>
  );
};

const ActionButtons: FC<{
  onEdit: () => void;
  onDelete: () => void;
}> = ({ onEdit, onDelete }) => {
  return (
    <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
      <IconButton size="small" onClick={onEdit}>
        <EditOutlinedIcon fontSize="small" />
      </IconButton>

      <IconButton size="small" color="error" onClick={onDelete}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const FilePreviewDialog: FC<{
  open: boolean;
  file: PreviewFile | null;
  onClose: () => void;
}> = ({ open, file, onClose }) => {
  const { t } = useTranslation();

  if (!file) return null;

  const pdf = isPdfUrl(file.url);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }} noWrap>
          {file.name}
        </Typography>

        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "78vh" }}>
        {pdf ? (
          <Box sx={{ height: "70vh" }}>
            <iframe
              title={file.name}
              src={file.url}
              style={{
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={file.url}
              alt={file.name}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          startIcon={<CloseIcon fontSize="small" />}
        >
          {t("close", "Close")}
        </Button>

        <Button
          component="a"
          href={file.url}
          target="_blank"
          rel="noreferrer"
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<DownloadIcon fontSize="small" />}
        >
          {t("download", "Download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog: FC<{
  open: boolean;
  target: DeleteTarget;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}> = ({ open, target, loading, onClose, onConfirm }) => {
  const { t } = useTranslation();

  const title =
    target?.type === "gasoil"
      ? t("deleteGasoilMeasurement", "Delete gasoil measurement")
      : t("deleteBunkering", "Delete bunkering");

  const description =
    target?.type === "gasoil"
      ? t("deleteGasoilMeasurementDescription", {
          defaultValue: "Are you sure you want to delete this gasoil measurement?",
        })
      : t("deleteBunkeringDescription", {
          defaultValue: "Are you sure you want to delete this bunkering?",
        });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 950 }}>{title}</DialogTitle>

      <DialogContent dividers>
        <Typography>{description}</Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t("cancel", "Cancel")}
        </Button>

        <Button
          color="error"
          variant="contained"
          disabled={loading}
          onClick={onConfirm}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 850,
            borderRadius: "10px",
          }}
        >
          {loading ? t("deleting", "Deleting") : t("delete", "Delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GasoilAndBunkeringPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const selectedVessel = useSelector(selectedFilteredVessel);
  const selectedVesselId = getVesselId(selectedVessel);
  const selectedVesselName = getVesselName(selectedVessel);
  const selectedVesselIri = useMemo(
    () => getApiIri("vessels", selectedVesselId),
    [selectedVesselId]
  );

  const [dashboard, setDashboard] = useState<VesselGasoilDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [openCreateGasoilDialog, setOpenCreateGasoilDialog] = useState(false);
  const [openEditGasoilDialog, setOpenEditGasoilDialog] = useState(false);
  const [openYearOverviewDialog, setOpenYearOverviewDialog] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] =
    useState<DashboardMeasurement | null>(null);

  const [openCreateBunkeringDialog, setOpenCreateBunkeringDialog] =
    useState(false);
  const [openEditBunkeringDialog, setOpenEditBunkeringDialog] = useState(false);
  const [selectedBunkering, setSelectedBunkering] =
    useState<DashboardBunkering | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(getVessels());
  }, [dispatch]);

  const refreshDashboard = useCallback(async () => {
    if (!selectedVesselIri) {
      setDashboard(null);
      setLoadError("");
      return;
    }

    try {
      setLoading(true);
      setLoadError("");

      const [gasoilResponse, bunkeringResponse] = await Promise.all([
        axios({
          method: "get",
          url: `${API_URL}/vessel_gasoils?vessel=${encodeURIComponent(
            selectedVesselIri
          )}&order[date]=desc`,
          headers: {
            accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        axios({
          method: "get",
          url: `${API_URL}/vessel_bunkerings?vessel=${encodeURIComponent(
            selectedVesselIri
          )}&order[date]=desc`,
          headers: {
            accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
      ]);

      const measurements = normalizeCollection(gasoilResponse.data);
      const bunkerings = normalizeCollection(bunkeringResponse.data);

      setDashboard(
        buildDashboard({
          vesselId: selectedVesselId,
          vesselName: selectedVesselName,
          measurements,
          bunkerings,
        })
      );
    } catch (error: any) {
      setLoadError(
        getApiErrorMessage(
          error,
          t("failedToLoadGasoilDashboard", "Failed to load gasoil dashboard.")
        )
      );
    } finally {
      setLoading(false);
    }
  }, [selectedVesselIri, selectedVesselId, selectedVesselName, t]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  const staleChip = useMemo(() => {
    const days = dashboard?.staleDays;

    if (days === null || days === undefined) return null;

    let color: "success" | "warning" | "error" = "success";

    if (days >= 7) {
      color = "error";
    } else if (days >= 2) {
      color = "warning";
    }

    return (
      <Chip
        color={color}
        label={
          days === 1
            ? t("lastMeasurementOneDayAgo", "Last measurement 1 day ago")
            : t("lastMeasurementDaysAgo", {
                count: days,
                defaultValue: `Last measurement ${days} days ago`,
              })
        }
        sx={{
          fontWeight: 850,
          borderRadius: "999px",
        }}
      />
    );
  }, [dashboard?.staleDays, t]);

  const openPreview = (file: PreviewFile) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    const deleteUrl = getDeleteUrl(deleteTarget);

    if (!deleteUrl) return;

    try {
      setDeleteLoading(true);

      await axios({
        method: "delete",
        url: deleteUrl,
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setDeleteTarget(null);
      await refreshDashboard();
    } catch (error: any) {
      setLoadError(
        getApiErrorMessage(
          error,
          t("failedToDelete", "Failed to delete selected item.")
        )
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const measurements = dashboard?.measurements ?? [];
  const bunkerings = dashboard?.bunkerings ?? [];

  const timelineItems = useMemo(() => {
    return buildTimelineItems({
      measurements,
      bunkerings,
      t,
    });
  }, [measurements, bunkerings, t]);

  const chartRows = useMemo(() => {
    return dashboard?.chart?.length
      ? dashboard.chart
      : buildWeeklyChartRows({
          measurements,
          bunkerings,
          weeks: 8,
        });
  }, [dashboard?.chart, measurements, bunkerings]);

  return (
    <Layout>
      <Box
        sx={{
          height: "calc(100vh - 110px)",
          display: "grid",
          gridTemplateColumns: "320px minmax(0, 1fr)",
          gap: 1.5,
          p: 1.5,
          overflow: "hidden",
          backgroundColor: "#f8fafc",
        }}
      >
        <GasoilTimeline items={timelineItems} loading={loading} t={t} />

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: WRAPPER_BORDER_RADIUS,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
              backgroundColor: "#f8fafc",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 2,
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                backgroundColor: "#ffffff",
                flexShrink: 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "1.45rem",
                    fontWeight: 950,
                    color: "#1e293b",
                  }}
                >
                  {t("gasoilAndBunkering", "Gasoil and bunkering")}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    mt: 0.25,
                    color: "text.secondary",
                    fontSize: "0.88rem",
                    fontWeight: 700,
                  }}
                >
                  {selectedVesselName || t("noVesselSelected", "No vessel selected")}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {staleChip}

                <Button
                  variant="outlined"
                  startIcon={<CalendarViewMonthOutlinedIcon />}
                  disabled={!selectedVesselId}
                  onClick={() => setOpenYearOverviewDialog(true)}
                  sx={{
                    minHeight: 38,
                    px: 1.5,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 850,
                    backgroundColor: "#fff",
                  }}
                >
                  {t("yearOverview", "Year overview")}
                </Button>
              </Stack>
            </Box>

            {loading ? <LinearProgress /> : null}

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                p: 2.5,
              }}
            >
              {!selectedVesselIri ? (
                <Alert severity="info">
                  {t(
                    "selectVesselToViewGasoil",
                    "Select a vessel to view gasoil and bunkering."
                  )}
                </Alert>
              ) : loadError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loadError}
                </Alert>
              ) : null}

              {loading && !dashboard ? (
                <Box
                  sx={{
                    minHeight: "45vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : dashboard ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <StatCard
                      title={t("currentOnboard", "Current onboard")}
                      icon={<OpacityOutlinedIcon />}
                      value={`${formatNumber(
                        dashboard.currentOnboard?.amount ?? 0
                      )} L`}
                      subtitle={
                        dashboard.currentOnboard?.date
                          ? t("asOfDate", {
                              date: formatDateTime(dashboard.currentOnboard.date),
                              defaultValue: `As of ${formatDateTime(
                                dashboard.currentOnboard.date
                              )}`,
                            })
                          : "—"
                      }
                      accentColor={GASOIL_COLOR}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <StatCard
                      title={t("lastBunkering", "Last bunkering")}
                      icon={<LocalGasStationOutlinedIcon />}
                      value={`+${formatNumber(
                        dashboard.lastBunkering?.amount ?? 0
                      )} L`}
                      subtitle={formatDateOnly(dashboard.lastBunkering?.date)}
                      secondaryLine={dashboard.lastBunkering?.supplier || "—"}
                      accentColor={BUNKERING_COLOR}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <GasoilChartCard rows={chartRows} />
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <SectionCard
                      title={t("gasoilMeasurements", "Gasoil measurements")}
                      footer={
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={() => {
                            setSelectedMeasurement(null);
                            setOpenCreateGasoilDialog(true);
                          }}
                          sx={{
                            textTransform: "none",
                            fontWeight: 850,
                            borderRadius: "10px",
                          }}
                        >
                          <AddIcon sx={{ mr: 0.5 }} />
                          {t("addMeasurement", "Add measurement")}
                        </Button>
                      }
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("dateTime", "Date and time")}</TableCell>
                            <TableCell>
                              {t("amountLitersShort", "Amount (L)")}
                            </TableCell>
                            <TableCell>{t("addedBy", "Added by")}</TableCell>
                            <TableCell align="right">
                              {t("actions", "Actions")}
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {measurements.length ? (
                            measurements.map((item) => (
                              <TableRow key={item["@id"] || item.id}>
                                <TableCell>{formatDateTime(item.date)}</TableCell>
                                <TableCell sx={{ fontWeight: 850 }}>
                                  {formatNumber(item.amount ?? 0)}
                                </TableCell>
                                <TableCell>{getUserLabel(item.user)}</TableCell>
                                <TableCell align="right">
                                  <ActionButtons
                                    onEdit={() => {
                                      setSelectedMeasurement(item);
                                      setOpenEditGasoilDialog(true);
                                    }}
                                    onDelete={() =>
                                      setDeleteTarget({ type: "gasoil", item })
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Alert severity="info">
                                  {t(
                                    "noGasoilMeasurements",
                                    "No gasoil measurements found."
                                  )}
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </SectionCard>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <SectionCard
                      title={t("bunkering", "Bunkering")}
                      footer={
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={() => {
                            setSelectedBunkering(null);
                            setOpenCreateBunkeringDialog(true);
                          }}
                          sx={{
                            textTransform: "none",
                            fontWeight: 850,
                            borderRadius: "10px",
                          }}
                        >
                          <AddIcon sx={{ mr: 0.5 }} />
                          {t("addBunkering", "Add bunkering")}
                        </Button>
                      }
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("date", "Date")}</TableCell>
                            <TableCell>{t("supplier", "Supplier")}</TableCell>
                            <TableCell>
                              {t("amountLitersShort", "Amount (L)")}
                            </TableCell>
                            {/* <TableCell>{t("document", "Document")}</TableCell> */}
                            <TableCell align="right">
                              {t("actions", "Actions")}
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {bunkerings.length ? (
                            bunkerings.map((item) => {
                              const fileUrl = getFileUrl(item.file);
                              const fileName = getFileName(
                                item.file,
                                t("documentFallback", "Document")
                              );

                              return (
                                <TableRow key={item["@id"] || item.id}>
                                  <TableCell>{formatDateOnly(item.date)}</TableCell>
                                  <TableCell>{item.supplier || "—"}</TableCell>
                                  <TableCell sx={{ fontWeight: 850 }}>
                                    {formatNumber(item.amount ?? 0)}
                                  </TableCell>
                                  {/* <TableCell>
                                    {item.file && fileUrl ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                          <InsertDriveFileOutlinedIcon fontSize="small" />
                                        }
                                        onClick={() =>
                                          openPreview({
                                            url: fileUrl,
                                            name: fileName,
                                          })
                                        }
                                        sx={{
                                          textTransform: "none",
                                          maxWidth: 220,
                                        }}
                                      >
                                        <Typography
                                          component="span"
                                          noWrap
                                          sx={{
                                            maxWidth: 140,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {fileName}
                                        </Typography>
                                      </Button>
                                    ) : (
                                      "—"
                                    )}
                                  </TableCell> */}
                                  <TableCell align="right">
                                    <ActionButtons
                                      onEdit={() => {
                                        setSelectedBunkering(item);
                                        setOpenEditBunkeringDialog(true);
                                      }}
                                      onDelete={() =>
                                        setDeleteTarget({
                                          type: "bunkering",
                                          item,
                                        })
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Alert severity="info">
                                  {t("noBunkerings", "No bunkerings found.")}
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </SectionCard>
                  </Grid>
                </Grid>
              ) : null}
            </Box>
          </Paper>
        </Box>
      </Box>

      <FilePreviewDialog
        open={previewOpen}
        file={previewFile}
        onClose={closePreview}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        target={deleteTarget}
        loading={deleteLoading}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <GasoilYearOverviewDialog
        open={openYearOverviewDialog}
        onClose={() => setOpenYearOverviewDialog(false)}
        vesselId={selectedVesselId}
        onMeasurementSaved={refreshDashboard}
      />

      <GasoilDialogForm
        open={openCreateGasoilDialog}
        onClose={() => {
          setOpenCreateGasoilDialog(false);
          setSelectedMeasurement(null);
        }}
        onSaved={refreshDashboard}
        vesselId={selectedVesselId}
        initialData={null}
      />

      <GasoilDialogForm
        open={openEditGasoilDialog}
        onClose={() => {
          setOpenEditGasoilDialog(false);
          setSelectedMeasurement(null);
        }}
        onSaved={refreshDashboard}
        vesselId={selectedVesselId}
        initialData={selectedMeasurement}
      />

      <BunkeringDialogForm
        open={openCreateBunkeringDialog}
        onClose={() => {
          setOpenCreateBunkeringDialog(false);
          setSelectedBunkering(null);
        }}
        onSaved={refreshDashboard}
        vesselId={selectedVesselId}
        initialData={null}
      />

      <BunkeringDialogForm
        open={openEditBunkeringDialog}
        onClose={() => {
          setOpenEditBunkeringDialog(false);
          setSelectedBunkering(null);
        }}
        onSaved={refreshDashboard}
        vesselId={selectedVesselId}
        initialData={selectedBunkering}
      />

      <Modal />
    </Layout>
  );
};

export default GasoilAndBunkeringPage;
